/**
 * CoraCreaCrafts Shopify Customer Privacy bridge
 *
 * Propagates an EXPLICIT visitor cookie choice to Shopify's Customer Privacy
 * API so the platform's Web Pixels Manager and app-injected pixels honor it.
 *
 * The theme's own gating (assets/cookie-consent.js, window.CoraConsent) is
 * unchanged; this module only mirrors the stored decision to the platform.
 *
 * Deliberate scope:
 *   accepted  -> grant consent through the API
 *   declined  -> withhold consent through the API
 *   undecided -> do nothing at all, leaving the store's current platform
 *                default untouched. Defaulting undecided visitors to
 *                "not granted" would block Shopify pixels for everyone who
 *                has not chosen yet, which is a business decision for the
 *                store owner, not a side effect of this fix.
 *
 * The API is not synchronously available. It is requested through
 * window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}])
 * and only then exposed as window.Shopify.customerPrivacy. Every failure mode
 * (no Shopify global, no loadFeatures, load error, throwing setter) is
 * swallowed so the banner and the theme's gating keep working.
 */
(function () {
  'use strict';

  const CONSENT_KEY = 'cora-cookie-consent';
  const ACCEPTED = 'accepted';
  const DECLINED = 'declined';
  const FEATURE = { name: 'consent-tracking-api', version: '0.1' };

  let lastApplied = null;

  function readStoredDecision() {
    try {
      const value = localStorage.getItem(CONSENT_KEY);
      return value === ACCEPTED || value === DECLINED ? value : null;
    } catch (_e) {
      return null;
    }
  }

  function withCustomerPrivacy(callback) {
    const shopify = window.Shopify;
    if (!shopify) return;

    const ready = shopify.customerPrivacy;
    if (ready && typeof ready.setTrackingConsent === 'function') {
      callback(ready);
      return;
    }

    if (typeof shopify.loadFeatures !== 'function') return;

    try {
      shopify.loadFeatures([FEATURE], function (error) {
        if (error) return;
        const api = window.Shopify && window.Shopify.customerPrivacy;
        if (api && typeof api.setTrackingConsent === 'function') callback(api);
      });
    } catch (_e) {
      /* loadFeatures unavailable or threw; leave platform state untouched */
    }
  }

  function applyDecision(decision) {
    // Undecided is not a decision. Never call the API for it.
    if (decision !== ACCEPTED && decision !== DECLINED) return;
    if (decision === lastApplied) return;
    lastApplied = decision;

    const granted = decision === ACCEPTED;
    withCustomerPrivacy(function (api) {
      try {
        api.setTrackingConsent(
          {
            analytics: granted,
            marketing: granted,
            preferences: granted,
            sale_of_data: granted,
          },
          function () {}
        );
      } catch (_e) {
        /* platform rejected the write; theme gating is unaffected */
      }
    });
  }

  function replayStoredDecision() {
    applyDecision(readStoredDecision());
  }

  function onGranted() {
    applyDecision(ACCEPTED);
  }
  function onDeclined() {
    applyDecision(DECLINED);
  }

  document.addEventListener('cora:consent:granted', onGranted);
  document.addEventListener('cora:consent:declined', onDeclined);

  // cookie-consent.js runs first (both are deferred, in order) and dispatches
  // its granted event before this listener exists, so the stored decision must
  // also be replayed directly on every page load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', replayStoredDecision);
  } else {
    replayStoredDecision();
  }

  window.CoraShopifyConsentBridge = {
    applyDecision: applyDecision,
    readStoredDecision: readStoredDecision,
    replayStoredDecision: replayStoredDecision,
    destroy: function () {
      document.removeEventListener('cora:consent:granted', onGranted);
      document.removeEventListener('cora:consent:declined', onDeclined);
      document.removeEventListener('DOMContentLoaded', replayStoredDecision);
    },
  };
})();
